import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import { GitBookUsers } from '@gitbook/api-client';
import { getUserDisplayName } from '@gitbook/app-database';
import { colors } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    type AlertKind,
    Avatar,
    type AvatarProps,
    type AvatarSize,
    resolveAvatarPixelSize,
    resolveAvatarSize,
    Tooltip,
} from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { getImageResizedURL } from '../../utils';
import { getAnonymousColors, getRealTimeColor } from './utils';

export type UserObject = ({ uid: string } | { id: string } | { objectID: string }) & {
    displayName?: string | null;
    photoURL?: string | null;
};

type AvatarData = {
    userId: string;
    displayName: string;
    photoURL: string | null;
};

export type UserId = string;

export type UserAvatarProps = Pick<
    AvatarProps,
    'tooltip' | 'tooltipLabel' | 'tooltipPrefix' | 'size' | 'className' | 'squared'
> & {
    /**
     * The user to display, can be a userId or a user object.
     */
    user: UserObject | UserId | null | undefined;

    /** Indicate that this user is active */
    realtime?: boolean;

    /** If the avatar is in a stack, add a theme-dependent border */
    inStack?: boolean;

    /**
     * Render a semantic status dot overlaid on the avatar (e.g. `success` for a
     * green dot). The dot gets a border matching the surrounding surface for contrast.
     */
    status?: AlertKind;

    /**
     * A badge overlaid at the avatar's corner, in place of the `status` dot. It takes the dot's
     * footprint, so avatars carrying either stay aligned. Wins over `status` when both are given.
     */
    badge?: React.ReactNode;

    /**
     * Render the GitBook agent avatar in its inverted (solid) variant. No effect on
     * non-agent users, which have no inverted styling.
     */
    inverted?: boolean;
};

/** The agent glyph as a share of its avatar, so every size reads as the same mark. */
const AGENT_GLYPH_RATIO = 0.625;

const ANONYMOUS_DATA: AvatarData = {
    userId: 'anonymous',
    displayName: 'Anonymous',
    photoURL: null,
};

/**
 * Avatar for an user.
 */
export function UserAvatar({
    ref,
    ...props
}: UserAvatarProps & { ref?: React.Ref<HTMLDivElement> }) {
    const { status, badge, inverted, ...avatarProps } = props;
    const avatar = extractAvatarDataFromUserProp(avatarProps.user);
    const userId = extractUserFromUserProp(avatarProps.user);

    const isAgent =
        avatarProps.user === GitBookUsers.GitbookAgent ||
        (typeof avatarProps.user === 'object' &&
            avatarProps.user &&
            'id' in avatarProps.user &&
            avatarProps.user.id === GitBookUsers.GitbookAgent);

    const avatarElement = isAgent ? (
        <GitBookAgentAvatar ref={ref} {...avatarProps} inverted={inverted} />
    ) : avatar ? (
        <UserAvatarWithData ref={ref} {...avatarProps} avatar={avatar} />
    ) : userId ? (
        <React.Suspense fallback={<UserAvatarSkeleton ref={ref} {...avatarProps} />}>
            <UserAvatarFetcher ref={ref} userId={userId} {...avatarProps} />
        </React.Suspense>
    ) : (
        <UserAvatarWithData ref={ref} avatar={ANONYMOUS_DATA} {...avatarProps} />
    );

    if (!status && !badge) {
        return avatarElement;
    }

    // Overlay the status dot here (rather than inside a specific branch) so it works
    // uniformly across the user, agent, and fetched-avatar variants.
    return (
        <span className="relative inline-flex shrink-0">
            {avatarElement}
            {badge ? (
                <span
                    aria-hidden
                    className={clsx(
                        'absolute right-[-15%] bottom-[-15%] flex items-center justify-center',
                        resolveStatusBadgeSize(avatarProps.size)
                    )}
                >
                    {badge}
                </span>
            ) : status ? (
                <AvatarStatusDot status={status} size={avatarProps.size} />
            ) : null}
        </span>
    );
}

/**
 * Avatar for GitBook Agent.
 */
export function GitBookAgentAvatar({
    ref,
    ...props
}: (Pick<UserAvatarProps, 'squared' | 'realtime' | 'inStack'> & {
    size?: AvatarSize | number;
    loading?: boolean;
    tooltip?: boolean;
    inverted?: boolean;
}) & { ref?: React.Ref<HTMLDivElement> }) {
    const { loading = false, realtime, inStack, squared, tooltip = true, inverted } = props;
    const size = resolveAvatarSize(props.size);
    return (
        <Tooltip disabled={!tooltip} label="GitBook Agent">
            <div
                ref={ref}
                className={clsx(
                    'flex items-center justify-center',
                    inverted
                        ? 'bg-neutral-solid text-[var(--neutral-3)]'
                        : 'bg-neutral-ui text-neutral-low',
                    size.dimensions,
                    // Mirrors the photo avatar's `borderColor`: in a stack the ring reads the surface a
                    // parent paints (Recipes/Patterns/Current Background), so it blends with a hovered or
                    // focused fill; realtime keeps the static ring, as the agent has no presence colour.
                    realtime
                        ? 'border-2 border-(--sp-color-border-avatar)'
                        : inStack
                          ? 'border-2 border-[var(--current-background,var(--sp-color-border-avatar))]'
                          : null,
                    squared ? 'rounded' : 'rounded-full'
                )}
                // A numeric size drives the `1em`-based dimensions via font-size (see resolveAvatarSize).
                style={{ fontSize: size.fontSize }}
            >
                <Icon.AIAgentAnimated
                    state={loading ? 'loading' : 'default'}
                    className="block"
                    // Without a size the avatar is 1em, and so is the icon's own default.
                    size={
                        props.size
                            ? Math.round(resolveAvatarPixelSize(props.size) * AGENT_GLYPH_RATIO)
                            : undefined
                    }
                />
            </div>
        </Tooltip>
    );
}

export function UserAvatarSkeleton({
    ref,
    ...props
}: (Pick<UserAvatarProps, 'squared' | 'realtime' | 'inStack'> & {
    size?: AvatarSize | number;
}) & { ref?: React.Ref<HTMLDivElement> }) {
    const { realtime, squared, inStack } = props;
    const size = resolveAvatarSize(props.size);
    return (
        <div
            ref={ref}
            aria-busy
            className={clsx(
                'shimmer',
                size.dimensions,
                realtime || inStack
                    ? 'border-[var(--current-background,var(--sp-color-border-avatar))]'
                    : null,
                squared ? 'rounded' : 'rounded-full'
            )}
            style={{
                fontSize: size.fontSize,
                ...(realtime || inStack ? { borderWidth: size.borderWidth } : {}),
            }}
        />
    );
}

const UserAvatarFetcher = function UserAvatarFetcher({
    ref,
    ...props
}: (Omit<UserAvatarProps, 'user'> & { userId: string; size?: AvatarSize | number }) & {
    ref?: React.Ref<HTMLDivElement>;
}) {
    const { data } = useSuspenseQuery(
        apiQuery('getUserById', { userId: props.userId }, { nullOnError: true })
    );

    const avatar: AvatarData = React.useMemo(() => {
        if (!data) {
            return { userId: props.userId, displayName: 'Anonymous', photoURL: null };
        }
        return {
            userId: props.userId,
            displayName: data.displayName,
            photoURL: data.photoURL ?? null,
        };
    }, [data, props.userId]);

    return <UserAvatarWithData ref={ref} {...props} avatar={avatar} />;
};

/**
 * Extract user data from the user prop.
 * Returns null if data are not available.
 */
function extractAvatarDataFromUserProp(userProp: UserAvatarProps['user']): AvatarData | null {
    if (!userProp) {
        return null;
    }

    if (typeof userProp === 'string') {
        return null;
    }

    // If the userObject is not complete, then we will fetch the user data.
    if (!('photoURL' in userProp) || !('displayName' in userProp)) {
        return null;
    }

    const id = extractUserFromUserProp(userProp);

    if (!id) {
        return null;
    }

    return {
        userId: id,
        displayName: getUserDisplayName(userProp),
        photoURL: userProp.photoURL ?? null,
    };
}

/**
 * Extract a user id when the api is both user.uid or user.id.
 */
function extractUserFromUserProp(userProp: UserAvatarProps['user']): string | null {
    if (!userProp) {
        return null;
    }

    if (typeof userProp === 'string') {
        return userProp;
    }

    if ('uid' in userProp && userProp.uid) {
        return userProp.uid;
    }

    if ('id' in userProp && userProp.id) {
        return userProp.id;
    }

    if ('objectID' in userProp && userProp.objectID) {
        return userProp.objectID;
    }

    return null;
}

const UserAvatarWithData = function UserAvatarWithDataRef({
    ref,
    ...props
}: (Omit<UserAvatarProps, 'user'> & { avatar: AvatarData }) & { ref?: React.Ref<HTMLDivElement> }) {
    const { avatar, realtime = false, inStack } = props;

    const colors = getRealTimeColor(avatar.userId);
    // In CI, hash on the display name rather than the user id so the color is
    // stable across Playwright workers (test users get worker-dependent ids).
    const fallbackColors = getAnonymousColors(
        process.env.GITBOOK_PREDICTABLE_UI ? avatar.displayName : avatar.userId
    );

    return (
        <Avatar
            ref={ref}
            displayName={avatar.displayName}
            photoURL={
                avatar.photoURL &&
                getImageResizedURL(avatar.photoURL, { width: resolveAvatarPixelSize(props.size) })
            }
            fallbackColors={fallbackColors}
            borderColor={
                // Passing a spine token as colors.base can not be a token, and preferable to keep the border styling in a single prop.
                // For `inStack` we read the cascading "current surface" token so the
                // border camouflages with whatever surface a parent (e.g. a focused
                // MenuItem) paints, falling back to the static avatar-border token
                // at the page root where no parent overrides.
                realtime
                    ? colors.base
                    : inStack
                      ? 'var(--current-background, var(--sp-color-border-avatar))'
                      : undefined
            }
            {...props}
        />
    );
};

/** Solid fill per {@link AlertKind}, mirroring the status dot colors used in spine's `Status`. */
const STATUS_DOT_BG: Record<AlertKind, string> = {
    default: 'bg-[var(--neutral-3)]',
    info: 'bg-[var(--info-solid)]',
    danger: 'bg-[var(--danger-solid)]',
    success: 'bg-[#4dde98]', // the green from shades.css is too dark
    merge: 'bg-[var(--merge-solid)]',
    upgrade: 'bg-[var(--upgrade-solid)]',
    warning: 'bg-[var(--warning-solid)]',
};

/**
 * Semantic status dot overlaid on the bottom-left corner of a {@link UserAvatar}.
 * The border camouflages with the surrounding surface (like the avatar border) so
 * the dot reads as floating above the avatar with a clean contrast ring.
 */
function AvatarStatusDot(props: { status: AlertKind; size: AvatarSize | number | undefined }) {
    return (
        <span
            aria-hidden
            className={clsx(
                'absolute -right-0.5 -bottom-0.5 box-content rounded-full border-2',
                'border-[var(--current-background,var(--sp-color-border-avatar))]',
                resolveStatusDotSize(props.size),
                STATUS_DOT_BG[props.status]
            )}
        />
    );
}

/**
 * Badge dimension per avatar size. A badge carries its own surface-colored ring inside its
 * viewBox, so it is sized against the shape *within* that padding: the glyph then reads at the
 * status dot's scale, with the ring sitting outside it where the dot's border would be.
 */
const STATUS_BADGE_SIZE: Record<AvatarSize, string> = {
    xs: 'size-3',
    sm: 'size-4',
    md: 'size-4.5',
    lg: 'size-5',
};

function resolveStatusBadgeSize(size: AvatarSize | number | undefined): string {
    return STATUS_BADGE_SIZE[resolveAvatarSizeName(size)];
}

/** Status dot dimension per avatar size, scaled to roughly a third of the avatar. */
const STATUS_DOT_SIZE: Record<AvatarSize, string> = {
    xs: 'size-1.5',
    sm: 'size-2',
    md: 'size-2.5',
    lg: 'size-3',
};

/** Scale the status dot to roughly a third of the avatar size. */
function resolveStatusDotSize(size: AvatarSize | number | undefined): string {
    return STATUS_DOT_SIZE[resolveAvatarSizeName(size)];
}

/** The named size bucket a pixel avatar size falls into, for the overlays scaled against it. */
function resolveAvatarSizeName(size: AvatarSize | number | undefined): AvatarSize {
    if (typeof size !== 'number') {
        return size ?? 'sm';
    }
    if (size <= 16) {
        return 'xs';
    }
    if (size <= 24) {
        return 'sm';
    }
    if (size <= 32) {
        return 'md';
    }
    return 'lg';
}

/**
 * Returns the color associated to a user based on its displayName.
 */
export function getUserColor(displayName: string): string {
    const colorOptions = [
        colors.primary.base,
        colors.green.base,
        colors.violet.base,
        colors.fuchsia.base,
        colors.orange.base,
    ];
    const alpha = displayName.toLowerCase().charCodeAt(0) - 97;
    const colorPosition = alpha % colorOptions.length;
    return colorOptions[colorPosition];
}
